import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { 
  Wrench, 
  Plus, 
  Search, 
  User, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  Loader2, 
  X, 
  ShieldCheck, 
  IndianRupee, 
  ArrowRightLeft, 
  RotateCcw,
  Tag,
  Hash,
  AlertTriangle,
  RefreshCw,
  ShieldAlert,
  History
} from 'lucide-react';

export interface ServiceEquipment {
  id: string;
  tenant_id: string;
  branch_id?: string | null;
  name: string;
  code?: string | null;
  serial?: string | null;
  ownership: string; // 'company' | 'professional'
  condition: string; // 'new' | 'good' | 'fair' | 'damaged'
  deposit_required: number;
  purchase_cost?: number | null;
  is_active: boolean;
  notes?: string | null;
  created_at?: string;
  // Dynamic fields from RPC or joins
  current_issue_id?: string | null;
  is_issued?: boolean;
  issued_to_professional_id?: string | null;
  issued_to_name?: string | null;
  deposit_held?: number | null;
  issued_at?: string | null;
}

export interface StaffOption {
  id: string;
  full_name: string;
  phone?: string | null;
}

export function ServicesEquipmentView({ branchId }: { branchId?: string | null } = {}) {
  const { currentTenantId, activeBranchId } = useAuth();
  const supabase = getSupabaseClient();

  const [loading, setLoading] = useState(true);
  const [equipmentList, setEquipmentList] = useState<ServiceEquipment[]>([]);
  const [professionals, setProfessionals] = useState<StaffOption[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'available' | 'issued'>('all');

  // Modals state
  const [showAddModal, setShowAddModal] = useState(false);
  const [issuingEquipment, setIssuingEquipment] = useState<ServiceEquipment | null>(null);
  const [returningEquipment, setReturningEquipment] = useState<ServiceEquipment | null>(null);

  // Add form fields
  const [addName, setAddName] = useState('');
  const [addCode, setAddCode] = useState('');
  const [addSerial, setAddSerial] = useState('');
  const [addOwnership, setAddOwnership] = useState('company');
  const [addCondition, setAddCondition] = useState('good');
  const [addDepositRequired, setAddDepositRequired] = useState('0');
  const [addPurchaseCost, setAddPurchaseCost] = useState('');
  const [addNotes, setAddNotes] = useState('');
  const [submittingAdd, setSubmittingAdd] = useState(false);

  // Issue form fields
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [issueDepositAmount, setIssueDepositAmount] = useState('0');
  const [issueDepositMode, setIssueDepositMode] = useState('cash');
  const [issueDepositWaived, setIssueDepositWaived] = useState(false);
  const [issueWaiveReason, setIssueWaiveReason] = useState('');
  const [issueNotes, setIssueNotes] = useState('');
  const [submittingIssue, setSubmittingIssue] = useState(false);

  // Return form fields
  const [returnCondition, setReturnCondition] = useState('good');
  const [returnDamageCharge, setReturnDamageCharge] = useState('0');
  const [returnRefundMode, setReturnRefundMode] = useState('cash');
  const [returnNotes, setReturnNotes] = useState('');
  const [submittingReturn, setSubmittingReturn] = useState(false);

  // Alerts & History state
  const [alerts, setAlerts] = useState<any[]>([]);
  const [historyEquipment, setHistoryEquipment] = useState<ServiceEquipment | null>(null);
  const [equipmentHistory, setEquipmentHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const effectiveBranchId = branchId || activeBranchId;

  // ----------------------------------------------------------------
  // Load Equipment Alerts via RPC
  // ----------------------------------------------------------------
  const fetchAlerts = useCallback(async () => {
    if (!supabase || !currentTenantId) return;
    try {
      const { data, error } = await supabase.rpc('get_service_job_alerts', {
        p_tenant_id: currentTenantId,
        p_branch_id: effectiveBranchId || null
      });
      if (!error && Array.isArray(data)) {
        setAlerts(data);
      } else {
        setAlerts([]);
      }
    } catch (e) {
      console.warn('get_service_job_alerts error:', e);
    }
  }, [supabase, currentTenantId, effectiveBranchId]);

  // ----------------------------------------------------------------
  // Open Equipment History Drawer via RPC
  // ----------------------------------------------------------------
  const openHistoryDrawer = async (eq: ServiceEquipment) => {
    if (!supabase) return;
    setHistoryEquipment(eq);
    setEquipmentHistory([]);
    setLoadingHistory(true);

    try {
      const { data, error } = await supabase.rpc('get_service_equipment_history', {
        p_equipment_id: eq.id
      });

      if (error) {
        toast.error('Failed to load history: ' + error.message);
      } else {
        setEquipmentHistory(Array.isArray(data) ? data : []);
      }
    } catch (err: any) {
      toast.error(err.message || 'Error loading equipment history');
    } finally {
      setLoadingHistory(false);
    }
  };

  // ----------------------------------------------------------------
  // Load Equipment List & Staff List
  // ----------------------------------------------------------------
  const loadData = useCallback(async () => {
    if (!supabase || !currentTenantId) return;
    setLoading(true);

    try {
      fetchAlerts();

      // 1. Fetch equipment list via RPC
      const { data: eqData, error: eqErr } = await supabase.rpc('get_service_equipment_list', {
        p_tenant_id: currentTenantId,
        p_branch_id: effectiveBranchId || undefined
      });

      if (eqErr) {
        throw eqErr;
      }

      setEquipmentList((eqData as ServiceEquipment[]) || []);

      // 2. Fetch active professionals for assignment
      const { data: prosData, error: prosErr } = await supabase
        .from('service_professionals')
        .select('id, full_name, phone')
        .eq('tenant_id', currentTenantId)
        .eq('is_active', true)
        .order('full_name');

      if (prosErr) {
        console.warn('Error fetching professionals:', prosErr);
      } else {
        setProfessionals(prosData || []);
      }
    } catch (err: any) {
      console.error('Error loading equipment:', err);
      toast.error(err.message || 'Failed to load equipment list');
    } finally {
      setLoading(false);
    }
  }, [supabase, currentTenantId, effectiveBranchId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ----------------------------------------------------------------
  // Handle Add Equipment
  // ----------------------------------------------------------------
  const handleSaveEquipment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !currentTenantId) return;

    if (!addName.trim()) {
      toast.error('Equipment name is required');
      return;
    }

    setSubmittingAdd(true);
    try {
      // Resolve valid branch
      let branchToUse = effectiveBranchId;
      if (!branchToUse) {
        const { data: bData } = await supabase
          .from('branches')
          .select('id')
          .eq('tenant_id', currentTenantId)
          .limit(1)
          .maybeSingle();
        branchToUse = bData?.id || null;
      }

      if (!branchToUse) {
        throw new Error('Please configure at least one branch before adding equipment.');
      }

      const { data, error } = await supabase.rpc('save_service_equipment', {
        p_tenant_id: currentTenantId,
        p_branch_id: branchToUse,
        p_name: addName.trim(),
        p_ownership: addOwnership,
        p_code: addCode.trim() || null,
        p_serial: addSerial.trim() || null,
        p_condition: addCondition,
        p_deposit_required: Number(addDepositRequired) || 0,
        p_purchase_cost: addPurchaseCost ? Number(addPurchaseCost) : null,
        p_notes: addNotes.trim() || null,
        p_is_active: true
      });

      if (error) {
        throw error;
      }

      toast.success('Equipment saved successfully!');
      setShowAddModal(false);
      setAddName('');
      setAddCode('');
      setAddSerial('');
      setAddDepositRequired('0');
      setAddPurchaseCost('');
      setAddNotes('');
      await loadData();
    } catch (err: any) {
      console.error('Save equipment error:', err);
      toast.error(err.message || 'Failed to save equipment');
    } finally {
      setSubmittingAdd(false);
    }
  };

  // ----------------------------------------------------------------
  // Handle Issue Equipment
  // ----------------------------------------------------------------
  const handleOpenIssueModal = (eq: ServiceEquipment) => {
    setIssuingEquipment(eq);
    setSelectedStaffId(professionals[0]?.id || '');
    setIssueDepositAmount(String(eq.deposit_required || 0));
    setIssueDepositMode('cash');
    setIssueDepositWaived(false);
    setIssueWaiveReason('');
    setIssueNotes('');
  };

  const handleIssueSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !issuingEquipment) return;

    if (!selectedStaffId) {
      toast.error('Please select a professional');
      return;
    }

    setSubmittingIssue(true);
    try {
      const { data, error } = await supabase.rpc('issue_service_equipment', {
        p_equipment_id: issuingEquipment.id,
        p_professional_id: selectedStaffId,
        p_deposit_amount: issueDepositWaived ? 0 : Number(issueDepositAmount) || 0,
        p_deposit_mode: issueDepositWaived ? 'waived' : issueDepositMode,
        p_deposit_waived: issueDepositWaived,
        p_waive_reason: issueDepositWaived ? issueWaiveReason.trim() : null,
        p_notes: issueNotes.trim() || null
      });

      if (error) {
        throw error;
      }

      toast.success(`Issued ${issuingEquipment.name} to staff member.`);
      setIssuingEquipment(null);
      await loadData();
    } catch (err: any) {
      console.error('Issue equipment error:', err);
      toast.error(err.message || 'Failed to issue equipment');
    } finally {
      setSubmittingIssue(false);
    }
  };

  // ----------------------------------------------------------------
  // Handle Return Equipment
  // ----------------------------------------------------------------
  const handleOpenReturnModal = (eq: ServiceEquipment) => {
    setReturningEquipment(eq);
    setReturnCondition(eq.condition || 'good');
    setReturnDamageCharge('0');
    setReturnRefundMode('cash');
    setReturnNotes('');
  };

  const handleReturnSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !returningEquipment) return;

    if (!returningEquipment.current_issue_id) {
      toast.error('No active issue record found for this equipment');
      return;
    }

    setSubmittingReturn(true);
    try {
      const { data, error } = await supabase.rpc('return_service_equipment', {
        p_issue_id: returningEquipment.current_issue_id,
        p_condition: returnCondition,
        p_damage_charge: Number(returnDamageCharge) || 0,
        p_refund_mode: returnRefundMode,
        p_notes: returnNotes.trim() || null
      });

      if (error) {
        throw error;
      }

      toast.success(`Returned ${returningEquipment.name} to inventory.`);
      setReturningEquipment(null);
      await loadData();
    } catch (err: any) {
      console.error('Return equipment error:', err);
      toast.error(err.message || 'Failed to return equipment');
    } finally {
      setSubmittingReturn(false);
    }
  };

  // Filtered Equipment List
  const filteredList = equipmentList.filter(eq => {
    if (filterStatus === 'available' && eq.is_issued) return false;
    if (filterStatus === 'issued' && !eq.is_issued) return false;

    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      eq.name.toLowerCase().includes(q) ||
      (eq.code && eq.code.toLowerCase().includes(q)) ||
      (eq.serial && eq.serial.toLowerCase().includes(q)) ||
      (eq.issued_to_name && eq.issued_to_name.toLowerCase().includes(q))
    );
  });

  const totalCount = equipmentList.length;
  const issuedCount = equipmentList.filter(e => e.is_issued).length;
  const availableCount = totalCount - issuedCount;
  const totalDepositHeld = equipmentList.reduce((sum, e) => sum + (Number(e.deposit_held) || 0), 0);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Wrench className="text-primary" size={24} />
            <span>Equipment & Tools Management</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Track equipment issues to technicians, security deposits & returns
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="text-xs h-9 gap-1.5"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </Button>

          <Button
            size="sm"
            onClick={() => setShowAddModal(true)}
            className="bg-primary hover:bg-primary/90 text-white text-xs h-9 gap-1.5 font-semibold"
          >
            <Plus size={14} />
            <span>Add Equipment</span>
          </Button>
        </div>
      </div>

      {/* Top Alerts Panel via get_service_job_alerts */}
      {alerts.length > 0 && (
        <div className="p-4 rounded-2xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/60 space-y-3 animate-in fade-in">
          <div className="flex items-center gap-2">
            <span className="p-1.5 bg-rose-500 text-white rounded-lg">
              <ShieldAlert size={16} />
            </span>
            <h3 className="text-sm font-bold text-rose-900 dark:text-rose-200">
              Equipment & Custody Alerts
            </h3>
            <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-rose-600 text-white">
              {alerts.length}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {alerts.map((al, idx) => (
              <div 
                key={idx}
                className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-rose-100 dark:border-rose-900/50 shadow-xs text-xs space-y-1.5"
              >
                <div className="flex items-center justify-between text-[10px] font-bold text-rose-600">
                  <span className="uppercase">{al.type.replace(/_/g, ' ')}</span>
                  {al.booking_number && (
                    <span className="font-mono text-slate-500">{al.booking_number}</span>
                  )}
                </div>
                <p className="font-medium text-slate-800 dark:text-slate-200">
                  {al.message}
                </p>
                <div className="text-[11px] text-slate-400 flex items-center justify-between pt-1 border-t border-slate-100 dark:border-slate-800">
                  <span>{al.professional || al.item || 'Service Item'}</span>
                  {al.since && <span>{new Date(al.since).toLocaleDateString()}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPI Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-3.5 sm:p-4">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">
              Total Equipment
            </span>
            <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mt-1">
              {totalCount}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-3.5 sm:p-4">
            <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider block">
              Available in Godown
            </span>
            <div className="text-xl sm:text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
              {availableCount}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-3.5 sm:p-4">
            <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider block">
              Issued to Staff
            </span>
            <div className="text-xl sm:text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">
              {issuedCount}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-3.5 sm:p-4">
            <span className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider block">
              Security Deposit Held
            </span>
            <div className="text-xl sm:text-2xl font-bold text-indigo-600 dark:text-indigo-400 mt-1 font-mono">
              ₹{totalDepositHeld.toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white dark:bg-slate-900 p-3 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
        <div className="relative w-full sm:w-80">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search equipment name, code, serial, staff..."
            className="pl-9 h-9 text-xs bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
          />
        </div>

        <div className="flex items-center gap-1.5 w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setFilterStatus('all')}
            className={`flex-1 sm:flex-none px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${
              filterStatus === 'all'
                ? 'bg-primary text-white border-primary shadow-xs'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-transparent hover:bg-slate-200'
            }`}
          >
            All ({totalCount})
          </button>
          <button
            type="button"
            onClick={() => setFilterStatus('available')}
            className={`flex-1 sm:flex-none px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${
              filterStatus === 'available'
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-transparent hover:bg-slate-200'
            }`}
          >
            Available ({availableCount})
          </button>
          <button
            type="button"
            onClick={() => setFilterStatus('issued')}
            className={`flex-1 sm:flex-none px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${
              filterStatus === 'issued'
                ? 'bg-amber-600 text-white border-amber-600 shadow-xs'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-transparent hover:bg-slate-200'
            }`}
          >
            Issued ({issuedCount})
          </button>
        </div>
      </div>

      {/* Equipment List Table / Cards */}
      {loading ? (
        <div className="p-12 text-center text-slate-400 text-sm flex flex-col items-center justify-center gap-2">
          <Loader2 size={24} className="animate-spin text-primary" />
          <span>Loading equipment and tools inventory...</span>
        </div>
      ) : filteredList.length === 0 ? (
        <div className="p-12 text-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
          <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto text-slate-400">
            <Wrench size={22} />
          </div>
          <h3 className="text-base font-bold text-slate-800 dark:text-white">
            {searchQuery ? 'No matching equipment found' : 'No equipment added yet'}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
            Add tools and devices (vacuum cleaners, drilling machines, AC gauges, kits) to track security deposits and staff issuance.
          </p>
          {!searchQuery && (
            <Button
              size="sm"
              onClick={() => setShowAddModal(true)}
              className="bg-primary text-white text-xs h-9 mt-2 font-semibold"
            >
              <Plus size={14} className="mr-1" />
              Add First Equipment
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredList.map(eq => (
            <Card
              key={eq.id}
              className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs hover:border-primary/40 transition-all rounded-2xl overflow-hidden flex flex-col"
            >
              <CardContent className="p-4 flex-1 flex flex-col justify-between space-y-3">
                {/* Header & Badges */}
                <div>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                        {eq.name}
                      </h3>
                      {eq.code && (
                        <span className="font-mono text-[10px] text-slate-400 block mt-0.5">
                          Code: {eq.code}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        eq.ownership === 'professional'
                          ? 'bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800'
                          : 'bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                      }`}>
                        {eq.ownership === 'professional' ? 'Professional ka apna' : 'Business'}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        eq.is_issued
                          ? 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800'
                          : 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'
                      }`}>
                        {eq.is_issued ? 'Issued' : 'Available'}
                      </span>
                    </div>
                  </div>

                  {/* Attributes Grid */}
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 dark:text-slate-300 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-800">
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase block font-semibold">Ownership</span>
                      <span className="font-semibold text-xs">
                        {eq.ownership === 'professional' ? 'Professional ka apna' : 'Business'}
                      </span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase block font-semibold">Condition</span>
                      <span className="font-medium capitalize">{eq.condition}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase block font-semibold">Deposit Req.</span>
                      <span className="font-bold text-slate-900 dark:text-white">₹{eq.deposit_required}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase block font-semibold">Serial #</span>
                      <span className="font-mono text-[11px] truncate block">{eq.serial || 'N/A'}</span>
                    </div>
                  </div>

                  {/* Issued info if currently with professional */}
                  {eq.is_issued && (
                    <div className="mt-2.5 p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300 space-y-1">
                      <div className="flex items-center gap-1.5 font-semibold text-amber-400">
                        <User size={13} />
                        <span>Issued to: {eq.issued_to_name || 'Staff Member'}</span>
                      </div>
                      <div className="text-[11px] text-amber-200/90 flex justify-between">
                        <span>Deposit Collected:</span>
                        <strong className="font-mono">₹{eq.deposit_held ?? eq.deposit_required}</strong>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer Action Buttons */}
                <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    type="button"
                    onClick={() => openHistoryDrawer(eq)}
                    className="text-xs h-8 px-2.5 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 font-semibold gap-1"
                  >
                    <History size={13} className="text-slate-400" />
                    <span>History</span>
                  </Button>

                  {eq.is_issued ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenReturnModal(eq)}
                      className="flex-1 text-xs h-8 border-amber-700/40 text-amber-400 hover:bg-amber-950/30 font-semibold gap-1"
                    >
                      <RotateCcw size={13} />
                      <span>Return & Refund Deposit</span>
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleOpenIssueModal(eq)}
                      className="flex-1 bg-primary hover:bg-primary/90 text-white text-xs h-8 font-semibold gap-1"
                    >
                      <ArrowRightLeft size={13} />
                      <span>Issue to Professional</span>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ======================================================== */}
      {/* 1. ADD EQUIPMENT MODAL                                   */}
      {/* ======================================================== */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-5 text-white animate-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800 mb-4">
              <h3 className="text-base font-bold flex items-center gap-2">
                <Wrench size={18} className="text-primary" />
                <span>Add Service Equipment</span>
              </h3>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSaveEquipment} className="space-y-3.5 text-xs">
              <div>
                <label className="font-semibold text-slate-300 block mb-1">
                  Equipment / Tool Name <span className="text-rose-500">*</span>
                </label>
                <Input
                  value={addName}
                  onChange={e => setAddName(e.target.value)}
                  placeholder="e.g. High Pressure Jet Washer, AC Vacuum Pump"
                  required
                  className="h-9 text-xs bg-slate-800 border-slate-700 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-semibold text-slate-300 block mb-1">
                    Asset / Item Code
                  </label>
                  <Input
                    value={addCode}
                    onChange={e => setAddCode(e.target.value)}
                    placeholder="e.g. EQ-001"
                    className="h-9 text-xs bg-slate-800 border-slate-700 text-white font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-300 block mb-1">
                    Serial Number
                  </label>
                  <Input
                    value={addSerial}
                    onChange={e => setAddSerial(e.target.value)}
                    placeholder="e.g. SN-8921820"
                    className="h-9 text-xs bg-slate-800 border-slate-700 text-white font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-semibold text-slate-300 block mb-1">
                    Ownership
                  </label>
                  <select
                    value={addOwnership}
                    onChange={e => setAddOwnership(e.target.value)}
                    className="w-full h-9 px-2 bg-slate-800 border border-slate-700 text-white text-xs rounded-lg outline-none"
                  >
                    <option value="company">Company Owned</option>
                    <option value="professional">Professional Owned</option>
                  </select>
                </div>
                <div>
                  <label className="font-semibold text-slate-300 block mb-1">
                    Condition
                  </label>
                  <select
                    value={addCondition}
                    onChange={e => setAddCondition(e.target.value)}
                    className="w-full h-9 px-2 bg-slate-800 border border-slate-700 text-white text-xs rounded-lg outline-none"
                  >
                    <option value="new">Brand New</option>
                    <option value="good">Good / Functional</option>
                    <option value="fair">Fair / Used</option>
                    <option value="damaged">Damaged / Needs Repair</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-semibold text-slate-300 block mb-1">
                    Security Deposit Required (₹)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={addDepositRequired}
                    onChange={e => setAddDepositRequired(e.target.value)}
                    placeholder="e.g. 1500"
                    className="h-9 text-xs bg-slate-800 border-slate-700 text-white font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-300 block mb-1">
                    Purchase Cost (₹)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={addPurchaseCost}
                    onChange={e => setAddPurchaseCost(e.target.value)}
                    placeholder="Optional"
                    className="h-9 text-xs bg-slate-800 border-slate-700 text-white font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-300 block mb-1">
                  Notes / Specifications
                </label>
                <Textarea
                  value={addNotes}
                  onChange={e => setAddNotes(e.target.value)}
                  placeholder="e.g. 150 bar pressure pump with 10m hose and nozzle"
                  rows={2}
                  className="text-xs bg-slate-800 border-slate-700 text-white resize-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddModal(false)}
                  disabled={submittingAdd}
                  className="border-slate-700 text-slate-300 text-xs h-9"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={submittingAdd}
                  className="bg-primary hover:bg-primary/90 text-white text-xs h-9 px-4 font-semibold"
                >
                  {submittingAdd ? <Loader2 size={13} className="animate-spin" /> : 'Save Equipment'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 2. ISSUE EQUIPMENT MODAL                                 */}
      {/* ======================================================== */}
      {issuingEquipment && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-5 text-white animate-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800 mb-4">
              <div>
                <h3 className="text-base font-bold flex items-center gap-2">
                  <ArrowRightLeft size={18} className="text-primary" />
                  <span>Issue Equipment</span>
                </h3>
                <p className="text-xs text-slate-400 mt-0.5 font-medium">
                  {issuingEquipment.name} ({issuingEquipment.code || 'No Code'})
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIssuingEquipment(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleIssueSubmit} className="space-y-3.5 text-xs">
              <div>
                <label className="font-semibold text-slate-300 block mb-1">
                  Assign to Professional / Technician <span className="text-rose-500">*</span>
                </label>
                <select
                  value={selectedStaffId}
                  onChange={e => setSelectedStaffId(e.target.value)}
                  required
                  className="w-full h-9 px-2 bg-slate-800 border border-slate-700 text-white text-xs rounded-lg outline-none"
                >
                  <option value="">Select staff member...</option>
                  {professionals.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.full_name} {p.phone ? `(${p.phone})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Waive deposit toggle */}
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-800/80 border border-slate-700">
                <div>
                  <span className="font-semibold block text-slate-200">Waive Security Deposit?</span>
                  <span className="text-[11px] text-slate-400">Issue without collecting cash/deposit</span>
                </div>
                <input
                  type="checkbox"
                  checked={issueDepositWaived}
                  onChange={e => setIssueDepositWaived(e.target.checked)}
                  className="w-4 h-4 rounded text-primary focus:ring-primary"
                />
              </div>

              {!issueDepositWaived ? (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="font-semibold text-slate-300 block mb-1">
                      Deposit Amount (₹)
                    </label>
                    <Input
                      type="number"
                      min="0"
                      value={issueDepositAmount}
                      onChange={e => setIssueDepositAmount(e.target.value)}
                      className="h-9 text-xs bg-slate-800 border-slate-700 text-white font-mono"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-slate-300 block mb-1">
                      Deposit Mode
                    </label>
                    <select
                      value={issueDepositMode}
                      onChange={e => setIssueDepositMode(e.target.value)}
                      className="w-full h-9 px-2 bg-slate-800 border border-slate-700 text-white text-xs rounded-lg outline-none"
                    >
                      <option value="cash">Cash Received</option>
                      <option value="upi">UPI / Online</option>
                      <option value="bank">Bank Transfer</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="font-semibold text-slate-300 block mb-1">
                    Waive Reason
                  </label>
                  <Input
                    value={issueWaiveReason}
                    onChange={e => setIssueWaiveReason(e.target.value)}
                    placeholder="e.g. Senior staff exemption / Company technician"
                    className="h-9 text-xs bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              )}

              <div>
                <label className="font-semibold text-slate-300 block mb-1">
                  Issue Remarks / Notes
                </label>
                <Textarea
                  value={issueNotes}
                  onChange={e => setIssueNotes(e.target.value)}
                  placeholder="e.g. Delivered with complete accessory kit and charger"
                  rows={2}
                  className="text-xs bg-slate-800 border-slate-700 text-white resize-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIssuingEquipment(null)}
                  disabled={submittingIssue}
                  className="border-slate-700 text-slate-300 text-xs h-9"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={submittingIssue}
                  className="bg-primary hover:bg-primary/90 text-white text-xs h-9 px-4 font-semibold"
                >
                  {submittingIssue ? <Loader2 size={13} className="animate-spin" /> : 'Confirm Issue'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 3. RETURN EQUIPMENT MODAL                                */}
      {/* ======================================================== */}
      {returningEquipment && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-5 text-white animate-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800 mb-4">
              <div>
                <h3 className="text-base font-bold flex items-center gap-2">
                  <RotateCcw size={18} className="text-amber-400" />
                  <span>Return Equipment</span>
                </h3>
                <p className="text-xs text-slate-400 mt-0.5 font-medium">
                  {returningEquipment.name} (Held Deposit: ₹{returningEquipment.deposit_held ?? 0})
                </p>
              </div>
              <button
                type="button"
                onClick={() => setReturningEquipment(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleReturnSubmit} className="space-y-3.5 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-semibold text-slate-300 block mb-1">
                    Return Condition
                  </label>
                  <select
                    value={returnCondition}
                    onChange={e => setReturnCondition(e.target.value)}
                    className="w-full h-9 px-2 bg-slate-800 border border-slate-700 text-white text-xs rounded-lg outline-none"
                  >
                    <option value="good">Good / Functional</option>
                    <option value="fair">Fair / Normal Wear</option>
                    <option value="damaged">Damaged / Broken Parts</option>
                  </select>
                </div>
                <div>
                  <label className="font-semibold text-slate-300 block mb-1">
                    Damage Charge (₹)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={returnDamageCharge}
                    onChange={e => setReturnDamageCharge(e.target.value)}
                    placeholder="0"
                    className="h-9 text-xs bg-slate-800 border-slate-700 text-white font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-300 block mb-1">
                  Deposit Refund Mode
                </label>
                <select
                  value={returnRefundMode}
                  onChange={e => setReturnRefundMode(e.target.value)}
                  className="w-full h-9 px-2 bg-slate-800 border border-slate-700 text-white text-xs rounded-lg outline-none"
                >
                  <option value="cash">Cash Refund</option>
                  <option value="upi">UPI / Online Transfer</option>
                  <option value="bank">Bank Account Transfer</option>
                  <option value="adjusted">Adjusted in Commission / Ledger</option>
                </select>
              </div>

              <div>
                <label className="font-semibold text-slate-300 block mb-1">
                  Inspection Remarks / Return Notes
                </label>
                <Textarea
                  value={returnNotes}
                  onChange={e => setReturnNotes(e.target.value)}
                  placeholder="e.g. Tool inspected and cleaned; motor running properly."
                  rows={2}
                  className="text-xs bg-slate-800 border-slate-700 text-white resize-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setReturningEquipment(null)}
                  disabled={submittingReturn}
                  className="border-slate-700 text-slate-300 text-xs h-9"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={submittingReturn}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-9 px-4 font-semibold"
                >
                  {submittingReturn ? <Loader2 size={13} className="animate-spin" /> : 'Confirm Return'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 4. EQUIPMENT HISTORY DRAWER                              */}
      {/* ======================================================== */}
      {historyEquipment && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div className="w-full max-w-lg bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
            {/* Header */}
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-900">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-primary/10 text-primary">
                    <History size={16} />
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-white text-base">
                    Equipment History
                  </h3>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="font-semibold text-slate-800 dark:text-slate-200">{historyEquipment.name}</span>
                  {historyEquipment.code && (
                    <span className="font-mono text-[11px] text-slate-400">({historyEquipment.code})</span>
                  )}
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    historyEquipment.ownership === 'professional'
                      ? 'bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300'
                      : 'bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300'
                  }`}>
                    {historyEquipment.ownership === 'professional' ? 'Professional ka apna' : 'Business'}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setHistoryEquipment(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X size={18} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loadingHistory ? (
                <div className="p-12 text-center text-slate-400 flex flex-col items-center justify-center gap-2">
                  <Loader2 size={24} className="animate-spin text-primary" />
                  <span className="text-xs">Loading equipment timeline & history...</span>
                </div>
              ) : equipmentHistory.length === 0 ? (
                <div className="p-12 text-center text-slate-400 space-y-2 border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
                  <Clock size={28} className="mx-auto text-slate-300 dark:text-slate-600" />
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">No History Recorded</p>
                  <p className="text-xs text-slate-400 max-w-xs mx-auto">
                    No issue or job assignment history recorded for this equipment yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {equipmentHistory.map((item: any, idx: number) => {
                    const isJob = item.kind === 'job';
                    const isIssue = item.kind === 'issue';
                    return (
                      <div
                        key={idx}
                        className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-800/50 space-y-2"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                            isJob 
                              ? 'bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                              : 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                          }`}>
                            {isJob ? 'Job Assignment' : 'Staff Custody / Issue'}
                          </span>

                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${
                            item.status === 'returned' || item.status === 'completed'
                              ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300'
                              : 'bg-indigo-100 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300'
                          }`}>
                            {item.status || 'Active'}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-[10px] text-slate-400 block font-semibold">Professional / Staff</span>
                            <span className="font-semibold text-slate-800 dark:text-slate-200">
                              {item.professional || 'Unassigned'}
                            </span>
                          </div>

                          {isJob && item.booking_number && (
                            <div>
                              <span className="text-[10px] text-slate-400 block font-semibold">Booking</span>
                              <span className="font-mono text-slate-800 dark:text-slate-200 font-semibold">
                                {item.booking_number}
                              </span>
                            </div>
                          )}

                          {isIssue && item.deposit !== undefined && item.deposit !== null && (
                            <div>
                              <span className="text-[10px] text-slate-400 block font-semibold">Deposit Held</span>
                              <span className="font-mono text-emerald-600 dark:text-emerald-400 font-bold">
                                ₹{item.deposit}
                              </span>
                            </div>
                          )}

                          <div>
                            <span className="text-[10px] text-slate-400 block font-semibold">Date Assigned / Issued</span>
                            <span className="text-slate-600 dark:text-slate-300 text-[11px]">
                              {item.at ? new Date(item.at).toLocaleString() : 'N/A'}
                            </span>
                          </div>

                          {item.returned_at && (
                            <div>
                              <span className="text-[10px] text-slate-400 block font-semibold">Returned At</span>
                              <span className="text-slate-600 dark:text-slate-300 text-[11px]">
                                {new Date(item.returned_at).toLocaleString()}
                              </span>
                            </div>
                          )}

                          {item.condition && (
                            <div>
                              <span className="text-[10px] text-slate-400 block font-semibold">Return Condition</span>
                              <span className={`font-semibold capitalize text-xs ${
                                item.condition === 'damaged' 
                                  ? 'text-rose-600 dark:text-rose-400' 
                                  : item.condition === 'fair'
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-emerald-600 dark:text-emerald-400'
                              }`}>
                                {item.condition}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-slate-200 dark:border-slate-800 flex justify-end bg-slate-50 dark:bg-slate-900">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setHistoryEquipment(null)}
                className="text-xs h-9 px-4"
              >
                Close History
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ServicesEquipmentView;
